// @ts-nocheck
/**
 * AlgoExecutionEngine — background React component (renders null).
 *
 * Runs the full algo condition execution loop on every page so that orders are
 * placed regardless of which route the user is on.  All notifications are
 * broadcast via:
 *   • CustomEvent  'algo_condition_notification'  (same-tab)
 *   • localStorage 'algo_condition_notification'  (cross-tab)
 *
 * Any state change that affects condition rows (trigger, reset, SL/Profit) is
 * also broadcast via CustomEvent 'algo_execution_event' so that the
 * AlgoConditions UI page can reload its conditions table.
 */

import { useEffect, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import api from '../services/api';

// ── helpers ───────────────────────────────────────────────────────────────────

const evalCondition = (current, operator, threshold) => {
  switch (operator) {
    case '<=': return current <= threshold;
    case '>=': return current >= threshold;
    case '<':  return current <  threshold;
    case '>':  return current >  threshold;
    default:   return false;
  }
};

// ── component ─────────────────────────────────────────────────────────────────

const AlgoExecutionEngine = () => {
  const { userId } = useSelector((s) => s.auth);

  // ── refs (stable, not causing re-renders) ─────────────────────────────────
  const conditionsRef           = useRef([]);   // current conditions array
  const qoeRef                  = useRef(null);  // latest qoe_live snapshot
  const firingRef               = useRef(new Set());
  const slProfitFiringRef       = useRef(new Set());
  const openPositionsRef        = useRef([]);
  const cooldownCheckInFlightRef = useRef(new Set());
  const userIdRef               = useRef(userId);

  // keep userIdRef in sync
  useEffect(() => { userIdRef.current = userId; }, [userId]);

  // ── Broadcast notification (no local state) ───────────────────────────────
  const broadcastNotification = useCallback((type, msg) => {
    const notification = { type, msg, ts: Date.now() };
    try {
      localStorage.setItem('algo_condition_notification', JSON.stringify(notification));
      window.dispatchEvent(new CustomEvent('algo_condition_notification', { detail: notification }));
    } catch (_) {}
  }, []);

  // ── Signal AlgoConditions UI to reload ───────────────────────────────────
  const signalUIReload = useCallback(() => {
    try {
      window.dispatchEvent(new CustomEvent('algo_execution_event'));
    } catch (_) {}
  }, []);

  // ── Post execution log to DB (no local setState) ──────────────────────────
  const addLogToDB = useCallback(async (entry) => {
    try {
      await api.post('/algo-conditions/logs', {
        userId:        userIdRef.current,
        conditionId:   entry.conditionId   ?? null,
        conditionName: entry.name,
        exchange:      entry.exchange,
        symbol:        entry.symbol,
        rowType:       entry.rowType,
        columnName:    entry.col,
        columnValue:   entry.value != null ? parseFloat(entry.value) : null,
        operator:      entry.operator      ?? null,
        triggerValue:  entry.triggerValue  ?? null,
        action:        entry.action,
        quantity:      entry.qty           ?? null,
        tradingSymbol: entry.tradingSymbol ?? null,
        orderId:       entry.orderId       ?? null,
        status:        entry.status,
        errorMessage:  entry.error         ?? null,
      });
    } catch (_) {}
  }, []);

  // ── Load conditions from DB ───────────────────────────────────────────────
  const loadConditions = useCallback(async () => {
    if (!userIdRef.current) return;
    try {
      const res = await api.get(`/algo-conditions?userId=${userIdRef.current}`);
      if (res.data.success) {
        const data = res.data.data;
        // Sync firingRef — clear ids that are no longer triggered
        data.forEach((c) => {
          if (!c.is_triggered) {
            firingRef.current.delete(c.id);
            slProfitFiringRef.current.delete(`${c.id}_SL`);
            slProfitFiringRef.current.delete(`${c.id}_PROFIT`);
          }
        });
        conditionsRef.current = data;
      }
    } catch (_) {}
  }, []);

  // Initial load + periodic refresh every 30 s
  useEffect(() => {
    if (!userId) return;
    loadConditions();
    const id = setInterval(loadConditions, 30000);
    return () => clearInterval(id);
  }, [userId, loadConditions]);

  // Listen for AlgoConditions UI CRUD changes → reload
  useEffect(() => {
    const onUIChange = () => loadConditions();
    window.addEventListener('algo_conditions_changed', onUIChange);
    return () => window.removeEventListener('algo_conditions_changed', onUIChange);
  }, [loadConditions]);

  // algoEnabledRef / algoModeRef are NOT used as React state — always read
  // localStorage directly in the interval so same-tab toggles are respected
  // immediately (window 'storage' event only fires for cross-tab changes).

  // ── Poll localStorage for live QOE values (500 ms) ───────────────────────
  useEffect(() => {
    const poll = () => {
      try {
        const raw = localStorage.getItem('qoe_live');
        if (raw) qoeRef.current = JSON.parse(raw);
      } catch (_) {}
    };
    poll();
    const id = setInterval(poll, 500);
    return () => clearInterval(id);
  }, []);

  // ── Helper: get current column value for a condition ─────────────────────
  const getCurrentValue = useCallback((cond, qoe) => {
    if (!qoe) return null;
    if (
      qoe.activeExchange?.toUpperCase() !== cond.exchange?.toUpperCase() ||
      qoe.activeSymbol?.toUpperCase()   !== cond.symbol?.toUpperCase()
    ) return null;
    const row = cond.row_type === 'CE' ? 'ce' : 'pe';
    switch (cond.column_name) {
      case 'strength': return qoe[`${row}Strength`] ?? null;
      case 'ltp':      return qoe[`${row}LTP`]      ?? null;
      case 'dif':      return qoe[`${row}Dif`]      ?? null;
      default:         return null;
    }
  }, []);

  // ── Place main order when condition triggers ───────────────────────────────
  const fireOrder = useCallback(async (cond, currentValue) => {
    const qoe = qoeRef.current;
    const uid = userIdRef.current;
    const strikeKey  = cond.row_type === 'CE' ? 'quickOrder_selectedCEStrike' : 'quickOrder_selectedPEStrike';
    const qtyKey     = cond.row_type === 'CE' ? 'quickOrder_ceQty'            : 'quickOrder_peQty';
    const strikeRaw  = localStorage.getItem(strikeKey);
    const strikeData = strikeRaw ? JSON.parse(strikeRaw) : null;
    const qty        = parseInt(localStorage.getItem(qtyKey) || '0');
    const orderType  = cond.order_type  || localStorage.getItem('quickOrder_orderType')  || 'MARKET';
    const product    = cond.product     || localStorage.getItem('quickOrder_productType') || 'MIS';

    const logBase = {
      time:          new Date().toLocaleTimeString(),
      name:          cond.name,
      conditionId:   cond.id,
      exchange:      cond.exchange,
      symbol:        cond.symbol,
      rowType:       cond.row_type,
      col:           cond.column_name,
      operator:      cond.operator,
      triggerValue:  cond.trigger_value,
      value:         currentValue?.toFixed(2),
      action:        cond.action,
      qty,
      tradingSymbol: strikeData?.tradingSymbol ?? null,
    };

    if (!strikeData) {
      firingRef.current.delete(cond.id);
      await addLogToDB({ ...logBase, status: 'FAILED', error: `No ${cond.row_type} strike selected in AlgoTrading` });
      broadcastNotification('error', `❌ ${cond.name}: No ${cond.row_type} strike selected`);
      return;
    }

    const resolvedInstrumentId = strikeData.instrumentId || strikeData.token;
    if (!resolvedInstrumentId) {
      firingRef.current.delete(cond.id);
      await addLogToDB({ ...logBase, status: 'FAILED', error: `Strike data missing instrumentId — re-select the ${cond.row_type} strike` });
      broadcastNotification('error', `❌ ${cond.name}: Strike has no instrumentId — re-select in AlgoTrading`);
      return;
    }
    if (!qty || qty <= 0) {
      firingRef.current.delete(cond.id);
      await addLogToDB({ ...logBase, status: 'FAILED', error: `Qty is 0 — set quantity in AlgoTrading QOE table` });
      broadcastNotification('error', `❌ ${cond.name}: Quantity is 0`);
      return;
    }

    try {
      const ltp     = cond.row_type === 'CE' ? qoe?.ceLTP : qoe?.peLTP;
      const payload = {
        exchange:        strikeData.exchange,
        tradingSymbol:   strikeData.tradingSymbol,
        instrumentId:    resolvedInstrumentId,
        transactionType: cond.action,
        quantity:        qty,
        price:           orderType === 'MARKET' ? 0 : parseFloat(ltp || 0),
        orderType,
        validity:        'DAY',
        product,
        orderComplexity: 'REGULAR',
        deviceId:        '123',
        userId:          uid,
        ltp:             parseFloat(ltp || 0),
      };

      const res     = await api.post('/orders/place', payload);
      const orderId = res.data?.data?.orderId || res.data?.orderId || null;

      await api.post(`/algo-conditions/${cond.id}/triggered`, {
        userId: uid,
        orderId,
        triggeredSymbol: strikeData.tradingSymbol,
      });

      // Update local conditions ref
      conditionsRef.current = conditionsRef.current.map((c) =>
        c.id === cond.id
          ? { ...c, is_triggered: 1, triggered_at: new Date().toISOString(), order_id: orderId, triggered_symbol: strikeData.tradingSymbol }
          : c
      );

      await addLogToDB({ ...logBase, orderId, status: 'SUCCESS', error: null });
      broadcastNotification('success', `✅ Auto-order: ${cond.action} ${qty} × ${strikeData.tradingSymbol}`);
      signalUIReload();
    } catch (err) {
      // Keep in firingRef to prevent infinite retries — user must reset manually
      await addLogToDB({ ...logBase, orderId: null, status: 'FAILED', error: err.message });
      broadcastNotification('error', `❌ ${cond.name}: ${err.message}`);
    }
  }, [addLogToDB, broadcastNotification, signalUIReload]);

  // ── Exit order for Stop Loss / Profit Booking ──────────────────────────────
  const fireExitOrder = useCallback(async (cond, exitType, currentValue) => {
    const key = `${cond.id}_${exitType}`;
    if (slProfitFiringRef.current.has(key)) return;
    slProfitFiringRef.current.add(key);

    const qoe        = qoeRef.current;
    const uid        = userIdRef.current;
    const exitAction = cond.action === 'BUY' ? 'SELL' : 'BUY';
    const strikeKey  = cond.row_type === 'CE' ? 'quickOrder_selectedCEStrike' : 'quickOrder_selectedPEStrike';
    const qtyKey     = cond.row_type === 'CE' ? 'quickOrder_ceQty'            : 'quickOrder_peQty';
    const strikeRaw  = localStorage.getItem(strikeKey);
    const strikeData = strikeRaw ? JSON.parse(strikeRaw) : null;
    const qty        = parseInt(localStorage.getItem(qtyKey) || '0');
    const orderType  = cond.order_type || 'MARKET';
    const product    = cond.product    || 'MIS';

    if (!strikeData || !qty || qty <= 0) {
      slProfitFiringRef.current.delete(key);
      broadcastNotification('error', `❌ ${exitType} exit failed: no strike or qty in AlgoTrading`);
      return;
    }
    const instrumentId = strikeData.instrumentId || strikeData.token;
    if (!instrumentId) {
      slProfitFiringRef.current.delete(key);
      broadcastNotification('error', `❌ ${exitType} exit failed: no instrumentId — re-select strike`);
      return;
    }

    try {
      const ltp     = cond.row_type === 'CE' ? qoe?.ceLTP : qoe?.peLTP;
      const payload = {
        exchange:        strikeData.exchange,
        tradingSymbol:   strikeData.tradingSymbol,
        instrumentId,
        transactionType: exitAction,
        quantity:        qty,
        price:           orderType === 'MARKET' ? 0 : parseFloat(ltp || 0),
        orderType,
        validity:        'DAY',
        product,
        orderComplexity: 'REGULAR',
        deviceId:        '123',
        userId:          uid,
        ltp:             parseFloat(ltp || 0),
      };

      const res     = await api.post('/orders/place', payload);
      const orderId = res.data?.data?.orderId || res.data?.orderId || null;

      const route = exitType === 'SL' ? 'sl-fired' : 'profit-fired';
      await api.post(`/algo-conditions/${cond.id}/${route}`, { userId: uid });

      // Update local conditions ref
      conditionsRef.current = conditionsRef.current.map((c) =>
        c.id === cond.id
          ? exitType === 'SL'
            ? { ...c, sl_fired: 1 }
            : { ...c, profit_fired: 1 }
          : c
      );

      const exitLabel = exitType === 'SL' ? '🛑 Stop Loss' : '💰 Profit';
      await addLogToDB({
        time:          new Date().toLocaleTimeString(),
        name:          `${cond.name} [${exitLabel}]`,
        conditionId:   cond.id,
        exchange:      cond.exchange,
        symbol:        cond.symbol,
        rowType:       cond.row_type,
        col:           exitType === 'SL' ? cond.sl_column   : cond.profit_column,
        operator:      exitType === 'SL' ? cond.sl_operator : cond.profit_operator,
        triggerValue:  exitType === 'SL' ? cond.sl_value    : cond.profit_value,
        value:         currentValue?.toFixed(2),
        action:        exitAction,
        qty,
        tradingSymbol: strikeData.tradingSymbol,
        orderId,
        status:        'SUCCESS',
        error:         null,
      });
      broadcastNotification('success', `${exitLabel} triggered: ${exitAction} ${qty} × ${strikeData.tradingSymbol}`);
      signalUIReload();
    } catch (err) {
      slProfitFiringRef.current.delete(key);
      broadcastNotification('error', `❌ ${exitType} exit order failed: ${err.message}`);
    }
  }, [addLogToDB, broadcastNotification, signalUIReload]);

  // ── Main condition check (runs every 500 ms via qoe poll) ─────────────────
  useEffect(() => {
    const checkInterval = setInterval(() => {
      if (localStorage.getItem('algoTrading_enabled') !== 'true') return;
      const qoe        = qoeRef.current;
      const conditions = conditionsRef.current;
      if (!qoe || conditions.length === 0) return;

      // Read the current algo mode (BUY / SELL / BOTH, default BOTH)
      const algoMode = localStorage.getItem('algoTrading_mode') || 'BOTH';

      const run = async () => {
        for (const cond of conditions) {

          // SL / Profit monitoring for triggered conditions — always runs, no mode restriction
          if (cond.is_triggered && cond.triggered_symbol) {
            const currentVal = getCurrentValue(cond, qoe);
            if (currentVal !== null) {
              const pos = openPositionsRef.current.find((p) => {
                const sym = p.tradingSymbol || p.tradingsymbol || '';
                return sym.toUpperCase() === cond.triggered_symbol.toUpperCase();
              });
              const buyQty  = parseFloat(pos?.buyQuantity  || pos?.buyQty  || pos?.dayBuyQuantity  || 0);
              const sellQty = parseFloat(pos?.sellQuantity || pos?.sellQty || pos?.daySellQuantity || 0);
              const netQty  = pos ? parseInt(pos.netQuantity || pos.netQty || pos.netqty || (buyQty - sellQty)) : 0;

              if (netQty !== 0) {
                if (cond.sl_enabled && !cond.sl_fired && cond.sl_value != null) {
                  const slMet = evalCondition(currentVal, cond.sl_operator, parseFloat(cond.sl_value));
                  if (slMet && !slProfitFiringRef.current.has(`${cond.id}_SL`)) {
                    await fireExitOrder(cond, 'SL', currentVal);
                  }
                }
                if (cond.profit_enabled && !cond.profit_fired && !cond.sl_fired && cond.profit_value != null) {
                  const profitMet = evalCondition(currentVal, cond.profit_operator, parseFloat(cond.profit_value));
                  if (profitMet && !slProfitFiringRef.current.has(`${cond.id}_PROFIT`)) {
                    await fireExitOrder(cond, 'PROFIT', currentVal);
                  }
                }
              }
            }
          }

          // Main condition check
          if (!cond.is_active || cond.is_triggered || firingRef.current.has(cond.id)) continue;

          // Mode filter: skip if the condition's type doesn't match the selected algo mode.
          // condition_type 'BOTH' always passes. Exit conditions (SL/Profit) are never skipped
          // (handled above) so this only gates the entry/main trigger.
          const condType = (cond.condition_type || 'BOTH').toUpperCase();
          if (algoMode === 'BUY'  && condType === 'SELL') continue;
          if (algoMode === 'SELL' && condType === 'BUY')  continue;

          const current   = getCurrentValue(cond, qoe);
          if (current === null) continue;

          const threshold = parseFloat(cond.trigger_value);
          if (!evalCondition(current, cond.operator, threshold)) continue;

          firingRef.current.add(cond.id);
          await fireOrder(cond, current);
        }
      };

      run();
    }, 500);

    return () => clearInterval(checkInterval);
  }, [getCurrentValue, fireOrder, fireExitOrder]);

  // ── Auto-reset: poll positions every 10 s for zero-cooldown conditions ────
  useEffect(() => {
    const pollPositions = async () => {
      const triggered = conditionsRef.current.filter(
        (c) => c.is_triggered && c.triggered_symbol
      );
      if (triggered.length === 0) return;

      try {
        const res = await api.get('/portfolio/positions');
        if (!res.data.success || !res.data.data) return;

        const openPositions = res.data.data;
        openPositionsRef.current = openPositions;

        for (const cond of triggered) {
          const pos = openPositions.find((p) => {
            const sym = p.tradingSymbol || p.tradingsymbol || '';
            return sym.toUpperCase() === cond.triggered_symbol.toUpperCase();
          });

          const buyQty  = parseFloat(pos?.buyQuantity  || pos?.buyQty  || pos?.dayBuyQuantity  || 0);
          const sellQty = parseFloat(pos?.sellQuantity || pos?.sellQty || pos?.daySellQuantity || 0);
          const netQty  = pos ? parseInt(pos.netQuantity || pos.netQty || pos.netqty || (buyQty - sellQty)) : 0;

          if (netQty === 0 && parseInt(cond.cooldown_seconds || 0) === 0) {
            try {
              await api.post(`/algo-conditions/${cond.id}/reset`, { userId: userIdRef.current });
              firingRef.current.delete(cond.id);
              conditionsRef.current = conditionsRef.current.map((c) =>
                c.id === cond.id
                  ? { ...c, is_triggered: 0, triggered_at: null, order_id: null, triggered_symbol: null }
                  : c
              );
              await addLogToDB({
                time:          new Date().toLocaleTimeString(),
                name:          cond.name,
                conditionId:   cond.id,
                exchange:      cond.exchange,
                symbol:        cond.symbol,
                rowType:       cond.row_type,
                col:           cond.column_name,
                operator:      cond.operator,
                triggerValue:  cond.trigger_value,
                value:         '0',
                action:        cond.action,
                qty:           0,
                tradingSymbol: cond.triggered_symbol || null,
                orderId:       null,
                status:        'SUCCESS',
                error:         'Auto-reset: position exited',
              });
              broadcastNotification('success', `♻️ "${cond.name}" re-armed — position exited, watching again`);
              signalUIReload();
            } catch (_) {}
          }
        }
      } catch (_) {}
    };

    const id = setInterval(pollPositions, 10000);
    pollPositions();
    return () => clearInterval(id);
  }, [addLogToDB, broadcastNotification, signalUIReload]);

  // ── Fast cooldown re-arm (1 s resolution) ─────────────────────────────────
  useEffect(() => {
    const checkCooldowns = async () => {
      const triggered = conditionsRef.current.filter(
        (c) => c.is_triggered && c.triggered_symbol && parseInt(c.cooldown_seconds || 0) > 0
      );
      if (triggered.length === 0) return;

      for (const cond of triggered) {
        if (cooldownCheckInFlightRef.current.has(cond.id)) continue;
        const elapsed = (Date.now() - new Date(cond.triggered_at).getTime()) / 1000;
        if (elapsed < parseInt(cond.cooldown_seconds)) continue;

        cooldownCheckInFlightRef.current.add(cond.id);
        try {
          const res = await api.get('/portfolio/positions');
          if (!res.data.success || !res.data.data) {
            cooldownCheckInFlightRef.current.delete(cond.id);
            continue;
          }

          openPositionsRef.current = res.data.data;

          const pos = res.data.data.find((p) => {
            const sym = p.tradingSymbol || p.tradingsymbol || '';
            return sym.toUpperCase() === cond.triggered_symbol.toUpperCase();
          });
          const buyQty  = parseFloat(pos?.buyQuantity  || pos?.buyQty  || pos?.dayBuyQuantity  || 0);
          const sellQty = parseFloat(pos?.sellQuantity || pos?.sellQty || pos?.daySellQuantity || 0);
          const netQty  = pos ? parseInt(pos.netQuantity || pos.netQty || pos.netqty || (buyQty - sellQty)) : 0;

          if (netQty === 0) {
            await api.post(`/algo-conditions/${cond.id}/reset`, { userId: userIdRef.current });
            firingRef.current.delete(cond.id);
            slProfitFiringRef.current.delete(`${cond.id}_SL`);
            slProfitFiringRef.current.delete(`${cond.id}_PROFIT`);
            conditionsRef.current = conditionsRef.current.map((c) =>
              c.id === cond.id
                ? { ...c, is_triggered: 0, triggered_at: null, order_id: null, triggered_symbol: null, sl_fired: 0, profit_fired: 0 }
                : c
            );
            await addLogToDB({
              time:          new Date().toLocaleTimeString(),
              name:          cond.name,
              conditionId:   cond.id,
              exchange:      cond.exchange,
              symbol:        cond.symbol,
              rowType:       cond.row_type,
              col:           cond.column_name,
              operator:      cond.operator,
              triggerValue:  cond.trigger_value,
              value:         '0',
              action:        cond.action,
              qty:           0,
              tradingSymbol: cond.triggered_symbol || null,
              orderId:       null,
              status:        'SUCCESS',
              error:         `Auto-reset after ${cond.cooldown_seconds}s cooldown`,
            });
            broadcastNotification('success', `♻️ "${cond.name}" re-armed after ${cond.cooldown_seconds}s cooldown`);
            signalUIReload();
          }
        } catch (_) {}
        cooldownCheckInFlightRef.current.delete(cond.id);
      }
    };

    const id = setInterval(checkCooldowns, 1000);
    return () => clearInterval(id);
  }, [addLogToDB, broadcastNotification, signalUIReload]);

  return null;
};

export default AlgoExecutionEngine;
